/**
 * Centralized API Client with automatic token refresh
 *
 * This module provides a fetch wrapper that:
 * - Automatically adds auth tokens to requests
 * - Handles token refresh on 401 responses
 * - Provides consistent error handling
 * - Prevents auth logic duplication across pages
 * - Supports mock data mode for Financial Hub demo
 *
 * API Base URL: Dedicated Azure Functions App
 */
import { useAuthStore } from '../stores/authStore';
import mockDataService from '../services/mockDataService';
import mockApiHandler from '../services/mockApiHandler';
import { activityTracker } from '../services/activityTracker';
// Track ongoing refresh to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise = null;
/**
 * Get the appropriate API base URL based on environment
 *
 * Always use production Azure Functions endpoint for consistency
 * This avoids the need to run local func start during development
 */
export const getApiBaseUrl = () => {
    return 'https://func-app-royalgem-b1.azurewebsites.net/api';
};
/**
 * Build full API URL from endpoint
 */
export const buildApiUrl = (endpoint) => {
    const baseUrl = getApiBaseUrl();
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${baseUrl}/${cleanEndpoint}`;
};
/**
 * Centralized authenticated fetch wrapper
 *
 * Features:
 * - Automatic token injection
 * - Token refresh on 401
 * - Consistent error handling
 * - Request retry after token refresh
 *
 * @param endpoint - API endpoint (e.g., 'users-list' or '/users-list')
 * @param options - Fetch options with auth extensions
 * @returns Promise with typed response
 */
export async function authFetch(endpoint, options = {}) {
    const { skipAuth = false, skipRefresh = false, ...fetchOptions } = options;
    // Check for mock mode - intercept with mock data for Financial Hub, Auction, and Shipping endpoints
    const isFinancialMockEndpoint = endpoint.startsWith('/suppliers') || endpoint.startsWith('suppliers') ||
        endpoint.startsWith('/purchases') || endpoint.startsWith('purchases') ||
        endpoint.startsWith('/payments') || endpoint.startsWith('payments') ||
        endpoint.startsWith('/invoices') || endpoint.startsWith('invoices') ||
        endpoint.startsWith('/supplier-payments') || endpoint.startsWith('supplier-payments') ||
        endpoint.startsWith('/auction') || endpoint.startsWith('auction');
    const isShippingMockEndpoint = endpoint.startsWith('/shipments') || endpoint.startsWith('shipments');
    const shouldMock = (mockDataService.isMockMode() && isFinancialMockEndpoint) ||
        (mockDataService.isShippingMockMode() && isShippingMockEndpoint);
    if (shouldMock) {
        const method = (fetchOptions.method || 'GET').toUpperCase();
        const body = fetchOptions.body ? JSON.parse(fetchOptions.body) : undefined;
        console.log(`🎭 MOCK MODE: ${method} ${endpoint}`);
        const mockResponse = await mockApiHandler.handleRequest(method, endpoint, body);
        return {
            ok: mockResponse.ok,
            status: mockResponse.ok ? 200 : 400,
            statusText: mockResponse.ok ? 'OK' : 'Bad Request',
            data: mockResponse.data,
            error: mockResponse.error,
            headers: new Headers()
        };
    }
    // Build full URL
    const url = buildApiUrl(endpoint);
    // Prepare headers
    const headers = new Headers(fetchOptions.headers);
    if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    // Add auth token if not skipped
    if (!skipAuth) {
        const { accessToken } = useAuthStore.getState();
        if (accessToken) {
            headers.set('Authorization', `Bearer ${accessToken}`);
        }
    }
    // Make the request
    console.log(`🌐 API Request: ${fetchOptions.method || 'GET'} ${url}`);
    const _apiStartTime = Date.now();
    try {
        const response = await fetch(url, {
            ...fetchOptions,
            headers
        });
        console.log(`🌐 API Response: ${response.status} ${url}`);
        // Track API call (skip activity-log and logs-query to avoid loops)
        activityTracker.trackApiCall(endpoint, fetchOptions.method || 'GET', response.status, Date.now() - _apiStartTime);
        // Handle 401 Unauthorized with token refresh
        if (response.status === 401 && !skipAuth && !skipRefresh) {
            const timestamp = new Date().toISOString();
            console.log(`🔄 [${timestamp}] apiClient - 401 Unauthorized detected for endpoint: ${endpoint}`);
            console.log(`🔄 [${timestamp}] apiClient - Attempting automatic token refresh...`);
            // Use existing refresh promise if one is in progress
            if (!refreshPromise) {
                console.log(`🔄 [${timestamp}] apiClient - No refresh in progress, starting new refresh`);
                isRefreshing = true;
                refreshPromise = useAuthStore.getState().refreshTokens();
            }
            else {
                console.log(`🔄 [${timestamp}] apiClient - Refresh already in progress, waiting for result`);
            }
            const refreshSuccess = await refreshPromise;
            // Reset refresh state
            refreshPromise = null;
            isRefreshing = false;
            if (refreshSuccess) {
                console.log(`✅ [${timestamp}] apiClient - Token refresh SUCCESS! Retrying original request...`);
                // Retry the request with new token
                const { accessToken } = useAuthStore.getState();
                if (accessToken) {
                    headers.set('Authorization', `Bearer ${accessToken}`);
                }
                const retryResponse = await fetch(url, {
                    ...fetchOptions,
                    headers
                });
                console.log(`✅ [${timestamp}] apiClient - Retry response: ${retryResponse.status} ${retryResponse.statusText}`);
                return parseResponse(retryResponse);
            }
            else {
                console.error(`❌ [${timestamp}] apiClient - Token refresh FAILED for endpoint: ${endpoint}`);
                console.error(`❌ [${timestamp}] apiClient - User will see session expired error`);
                return {
                    ok: false,
                    status: 401,
                    statusText: 'Unauthorized',
                    error: 'Session expired. Please log in again.',
                    headers: response.headers
                };
            }
        }
        return parseResponse(response);
    }
    catch (error) {
        console.error('🌐 API Request failed:', error);
        return {
            ok: false,
            status: 0,
            statusText: 'Network Error',
            error: error instanceof Error ? error.message : 'Unknown network error',
            headers: new Headers()
        };
    }
}
/**
 * Parse fetch response into standardized format
 */
async function parseResponse(response) {
    const contentType = response.headers.get('content-type');
    let data;
    let error;
    try {
        if (contentType?.includes('application/json')) {
            const json = await response.json();
            if (response.ok) {
                data = json;
            }
            else {
                // Extract error message from various API response formats
                error = json.error || json.message || `HTTP ${response.status}: ${response.statusText}`;
            }
        }
        else {
            const text = await response.text();
            // Try JSON parsing as fallback (Azure Functions may not always set Content-Type)
            let parsed = false;
            if (text && (text.startsWith('{') || text.startsWith('['))) {
                try {
                    const json = JSON.parse(text);
                    if (response.ok) {
                        data = json;
                    }
                    else {
                        error = json.error || json.message || `HTTP ${response.status}: ${response.statusText}`;
                    }
                    parsed = true;
                }
                catch {
                    // Not valid JSON, fall through to text handling
                }
            }
            if (!parsed) {
                if (response.ok) {
                    data = text;
                }
                else {
                    error = text || `HTTP ${response.status}: ${response.statusText}`;
                }
            }
        }
    }
    catch (parseError) {
        console.error('Failed to parse response:', parseError);
        error = 'Failed to parse server response';
    }
    return {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        data,
        error,
        headers: response.headers
    };
}
/**
 * Convenience methods for common HTTP verbs
 */
export const apiClient = {
    /**
     * GET request
     */
    get: (endpoint, options) => authFetch(endpoint, { ...options, method: 'GET' }),
    /**
     * POST request
     */
    post: (endpoint, body, options) => authFetch(endpoint, {
        ...options,
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined
    }),
    /**
     * PUT request
     */
    put: (endpoint, body, options) => authFetch(endpoint, {
        ...options,
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined
    }),
    /**
     * PATCH request
     */
    patch: (endpoint, body, options) => authFetch(endpoint, {
        ...options,
        method: 'PATCH',
        body: body ? JSON.stringify(body) : undefined
    }),
    /**
     * DELETE request
     */
    delete: (endpoint, options) => authFetch(endpoint, { ...options, method: 'DELETE' }),
    /**
     * Build API URL (utility for external use)
     */
    buildUrl: buildApiUrl
};
/**
 * Hook for easy API access in React components
 *
 * Usage:
 * ```tsx
 * const api = useApiClient();
 * const response = await api.get('/users-list');
 * if (response.ok) {
 *   setUsers(response.data.users);
 * } else {
 *   setError(response.error);
 * }
 * ```
 */
export const useApiClient = () => apiClient;
export default apiClient;
